import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import CustomDatePicker from 'Components/CustomDatePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import DeleteDialog from 'Components/DeleteDialog'
import FieldErrors from 'Components/FieldErrors'
import MultiSelect from 'Components/MultiSelect/MultiSelect'
import PageTitle from 'Components/PageTitle'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import RichTextEditor from 'Components/TextEditor'
import { WithLoader } from 'Components/WithLoader'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import { AffiliateNotificationPreviewDialog } from 'Services/SalesManagement/Features/Notification/Components/AffiliateNotificationPreviewDialog'
import { LogDialog } from 'Services/SalesManagement/Features/Notification/Components/LogDialog'
import { IMAGE_UPLOAD_URL } from 'Services/SalesManagement/Features/Notification/constants'
import {
  type AffiliateNotificationDetailShape,
  changeAffiliateNotification,
  createAffiliateNotification,
  deleteAffiliateNotification,
  fetchAffiliateNotification,
  fetchAffiliateNotificationOptions,
  NotificationType
} from 'Services/SalesManagement/Features/Notification/notificationSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { dateFormatter } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import type ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Tab,
  TextField,
  Typography
} from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { type FormEvent, type default as React, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

export function AffiliateNotificationDetailPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/notifications#${NotificationType.affiliate}`
  const previousLocation = usePreviousLocation(baseUrl)
  const dispatch = useAppDispatch()
  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)
  const [tabValue, setTabValue] = useState('fi')
  const initialAffiliateNotificationDetails: AffiliateNotificationDetailShape = {
    id: null,
    active_from: null,
    active_to: null,
    type: '',
    title_en: '',
    title_fi: '',
    title_sv: '',
    content_en: '',
    content_fi: '',
    content_sv: '',
    operators: [],
    edit_events: []
  }

  const [notificationDetails, setNotificationDetails] = useState<AffiliateNotificationDetailShape>(
    initialAffiliateNotificationDetails
  )

  const fieldErrorsDefaultState = {
    active_from: [],
    active_to: [],
    type: [],
    title_en: [],
    title_fi: [],
    title_sv: [],
    content_en: [],
    content_fi: [],
    content_sv: [],
    operators: []
  }

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleteActionLoading, setIsDeleteActionLoading] = useState(false)
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const [fiEditor, setFiEditor] = useState<ClassicEditor>()
  const [enEditor, setEnEditor] = useState<ClassicEditor>()
  const [svEditor, setSvEditor] = useState<ClassicEditor>()

  const navigate = useNavigate()

  const isNewInstance = Number.isNaN(instanceIdParam)

  const pageTitle = isNewInstance
    ? t`Create new affiliate notification`
    : t`Edit affiliate notification`

  const { affiliateNotificationOptions, loading, submitLoading } = useTypedSelector(
    state => state.notification
  )

  useEffect(() => {
    dispatch(fetchAffiliateNotificationOptions())
    if (isNewInstance) {
      setNotificationDetails(initialAffiliateNotificationDetails)
    } else {
      const params = {
        id: instanceIdParam
      }
      dispatch(fetchAffiliateNotification(params))
        .then(unwrapResult)
        .then(res => {
          setNotificationDetails(res)
        })
        .catch(() => {
          SnackbarUtils.error(t`There was an error fetching the notification.`)
        })
    }
  }, [dispatch, isNewInstance])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Notifications`,
      link: baseUrl
    },
    {
      title: pageTitle
    }
  ]

  const handleSelectChange = (
    event: SelectChangeEvent,
    name: keyof AffiliateNotificationDetailShape
  ) => {
    const { value } = event.target
    setNotificationDetails(prevState => ({ ...prevState, [name]: value }))
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    // save editor content between tab changes
    saveEditorContent(tabValue)
    setTabValue(newValue)
  }

  const handleDatePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value).toString()
    }
    setNotificationDetails(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const onSaveClick = () => {
    saveEditorContent(tabValue)
    setIsSaveDialogOpen(true)
  }

  const handleSubmit = () => {
    formRef.current?.requestSubmit()
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isNewInstance) {
      dispatch(createAffiliateNotification(notificationDetails))
        .then(unwrapResult)
        .then(() => {
          setIsSaveDialogOpen(false)
          navigate(baseUrl)
          SnackbarUtils.success(t`Affiliate notification successfully added.`)
        })
        .catch(error => {
          if (error) {
            setIsSaveDialogOpen(false)
            setFieldErrors({
              ...error
            })
          }
          SnackbarUtils.error(t`There was an error creating the notification.`)
        })
    } else {
      dispatch(changeAffiliateNotification(notificationDetails))
        .then(unwrapResult)
        .then(() => {
          setIsSaveDialogOpen(false)
          navigate(baseUrl)
          SnackbarUtils.success(t`Affiliate notification successfully updated.`)
        })
        .catch(error => {
          if (error) {
            setIsSaveDialogOpen(false)
            setFieldErrors({
              ...error
            })
          }
          SnackbarUtils.error(t`There was an error editing the notification.`)
        })
    }
  }

  const onDelete = () => {
    setIsDeleteDialogOpen(!isDeleteDialogOpen)
  }

  const handleDelete = () => {
    const params = {
      id: instanceIdParam
    }
    setIsDeleteActionLoading(true)
    dispatch(deleteAffiliateNotification(params))
      .then(unwrapResult)
      .then(() => {
        setIsDeleteDialogOpen(false)
        SnackbarUtils.success(t`Affiliate notification successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the notification.`)
      })
      .finally(() => {
        setIsDeleteActionLoading(false)
        navigate(baseUrl)
      })
  }

  const viewLog = () => {
    setIsLogDialogOpen(true)
  }
  const closeLog = () => {
    setIsLogDialogOpen(false)
  }

  const handleTitleOnBlur = (text: string, field: string) => {
    setNotificationDetails(prevState => ({ ...prevState, [field]: text }))
  }

  const saveEditorContent = (lang: string) => {
    const field = `content_${lang}`
    let editor = fiEditor
    if (lang === 'sv') {
      editor = svEditor
    } else if (lang === 'en') {
      editor = enEditor
    }

    // This means that the user has not visited the tab yet
    if (!editor) {
      return
    }

    const content = editor.getData()

    setNotificationDetails(prevState => ({ ...prevState, [field]: content }))
  }

  const handleMultiSelectChange = (
    selected: string[],
    name: keyof AffiliateNotificationDetailShape
  ) => {
    setNotificationDetails(prevState => ({ ...prevState, [name]: selected }))
  }

  const isLoading =
    loading === 'pending' ||
    (!isNewInstance &&
      (!notificationDetails.id || affiliateNotificationOptions.operators.length == 0))

  const isSubmitLoading = submitLoading === 'pending'

  const user = useTypedSelector(state => state.user.user)

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Breadcrumbs items={breadcrumbs} />
          <BackButton label={t`Back`} link={previousLocation} />
          <Box
            sx={{
              display: 'flex',
              maxWidth: 800,
              justifyContent: 'space-between',
              alignItems: 'start'
            }}
          >
            <PageTitle>{pageTitle}</PageTitle>
            {!isNewInstance && (
              <>
                <DeleteButton onClick={onDelete}>{t`Delete notification`}</DeleteButton>
                <DeleteDialog
                  open={isDeleteDialogOpen}
                  handleClose={() => setIsDeleteDialogOpen(false)}
                  handleSubmit={handleDelete}
                  loading={isDeleteActionLoading}
                  title={t`Delete notification`}
                >
                  <Typography component="span" color="text.primary">
                    {t`Are you sure you want to delete the notification?`}
                  </Typography>
                </DeleteDialog>
              </>
            )}
          </Box>
          {!isNewInstance && isSuperuser(user) && (
            <>
              <CustomButton size="small" onClick={viewLog}>
                {t`View log`}
              </CustomButton>
              <LogDialog
                open={isLogDialogOpen}
                handleClose={closeLog}
                log={notificationDetails.edit_events}
              />
            </>
          )}
        </Grid>
      </Grid>
      <WithLoader loading={isLoading}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <StyledForm onSubmit={handleSave} formRef={formRef} isDisabled={isSubmitLoading}>
              <Box sx={{ minWidth: 200, maxWidth: 400 }}>
                <MultiSelect
                  handleChange={selected => handleMultiSelectChange(selected, 'operators')}
                  label={t`Operator`}
                  required={false}
                  options={affiliateNotificationOptions.operators}
                  initialValues={
                    isNewInstance
                      ? []
                      : notificationDetails.operators.map(operator => [operator, operator])
                  }
                />
                <FieldErrors error={fieldErrors.operators} />
              </Box>
              <Box>
                <FormControl sx={{ minWidth: 200 }} required>
                  <InputLabel>{t`Type`}</InputLabel>
                  <Select
                    value={notificationDetails.type}
                    label={t`Type`}
                    autoWidth
                    onChange={e => handleSelectChange(e, 'type')}
                    error={fieldErrors.type?.length > 0}
                  >
                    {affiliateNotificationOptions.type.map(option => (
                      <MenuItem key={option[0]} value={option[0]}>
                        {option[1]}
                      </MenuItem>
                    ))}
                  </Select>
                  <FieldErrors error={fieldErrors.type} />
                </FormControl>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <CustomDatePicker
                    name="active_from"
                    value={
                      notificationDetails.active_from
                        ? new Date(notificationDetails.active_from)
                        : null
                    }
                    dateToCompare={
                      notificationDetails.active_to ? new Date(notificationDetails.active_to) : null
                    }
                    dateToCompareFieldName="active_to"
                    isStartDate={true}
                    onChange={handleDatePickerChange}
                    label={t`Active from`}
                  />
                  <Box>{` - `}</Box>
                  <CustomDatePicker
                    name="active_to"
                    value={
                      notificationDetails.active_to ? new Date(notificationDetails.active_to) : null
                    }
                    dateToCompare={
                      notificationDetails.active_from
                        ? new Date(notificationDetails.active_from)
                        : null
                    }
                    dateToCompareFieldName="active_from"
                    isStartDate={false}
                    onChange={handleDatePickerChange}
                    label={t`Active to`}
                  />
                </Box>
              </Box>
              <Box>
                <StyledLabel>{t`Content`}</StyledLabel>
                <TabContext value={tabValue}>
                  <Box sx={{ backgroundColor: 'background.paper' }}>
                    <Paper>
                      <TabList onChange={handleTabChange} aria-label={t`Notification tab list`}>
                        <Tab label="FI" value="fi" />
                        <Tab label="SV" value="sv" />
                        <Tab label="EN" value="en" />
                      </TabList>
                    </Paper>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="fi">
                      <Box sx={{ px: 2, pt: 4, pb: 1 }}>
                        <TextField
                          fullWidth
                          sx={{ backgroundColor: 'background.default' }}
                          id="title_fi"
                          defaultValue={notificationDetails.title_fi}
                          onBlur={event => handleTitleOnBlur(event.target.value, 'title_fi')}
                          label={t`Title` + ' (fi)'}
                          variant="outlined"
                        />
                        <FieldErrors error={fieldErrors.title_fi} />
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={notificationDetails.content_fi ?? ''}
                          setEditor={setFiEditor}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          editor={fiEditor}
                          imageUploadUrl={IMAGE_UPLOAD_URL}
                        />
                        <FieldErrors error={fieldErrors.content_fi} />
                      </Box>
                    </TabPanel>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="sv">
                      <Box sx={{ px: 2, pt: 4, pb: 1 }}>
                        <TextField
                          fullWidth
                          sx={{ backgroundColor: 'background.default' }}
                          id="title_sv"
                          defaultValue={notificationDetails.title_sv}
                          onBlur={event => handleTitleOnBlur(event.target.value, 'title_sv')}
                          label={t`Title` + ' (sv)'}
                          variant="outlined"
                        />
                        <FieldErrors error={fieldErrors.title_sv} />
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={notificationDetails.content_sv ?? ''}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          setEditor={setSvEditor}
                          editor={svEditor}
                          imageUploadUrl={IMAGE_UPLOAD_URL}
                        />
                        <FieldErrors error={fieldErrors.content_sv} />
                      </Box>
                    </TabPanel>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="en">
                      <Box sx={{ px: 2, pt: 4, pb: 1 }}>
                        <TextField
                          fullWidth
                          sx={{ backgroundColor: 'background.default' }}
                          id="title_en"
                          defaultValue={notificationDetails.title_en}
                          onBlur={event => handleTitleOnBlur(event.target.value, 'title_en')}
                          label={t`Title` + ' (en)'}
                          variant="outlined"
                        />
                        <FieldErrors error={fieldErrors.title_en} />
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={notificationDetails.content_en ?? ''}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          setEditor={setEnEditor}
                          editor={enEditor}
                          imageUploadUrl={IMAGE_UPLOAD_URL}
                        />
                        <FieldErrors error={fieldErrors.content_en} />
                      </Box>
                    </TabPanel>
                  </Box>
                </TabContext>
              </Box>
              <AffiliateNotificationPreviewDialog
                open={isSaveDialogOpen}
                handleClose={() => setIsSaveDialogOpen(false)}
                handleSubmit={handleSubmit}
                loading={submitLoading === 'pending'}
                isNewInstance={isNewInstance}
                notificationDetails={notificationDetails}
              />
              <CustomLoadingButton
                isLoading={isSubmitLoading}
                onClick={onSaveClick}
                size="large"
                iconPosition="end"
              >
                {t`Save notification`}
              </CustomLoadingButton>
            </StyledForm>
          </Grid>
        </Grid>
      </WithLoader>
    </Box>
  )
}
